import styled from '@xstyled/styled-components';
import React, { ChangeEventHandler } from 'react';
import Icon from './star.svg';

const ActiveIcon = styled(Icon)`
  color: var(--color-accent-fg);
  stroke: var(--color-accent-fg);
  fill: var(--color-accent-fg);
`;

/**
 * CAT-1378 / EL–E194 (WCAG 1.4.11): unselected star outline was #e0e0e0 on
 * white (1.32:1). greyDark (#6f6f6f / dark #efefef) matches Capsule/ProgressBar
 * and clears the 3:1 non-text contrast floor.
 */
const InactiveIcon = styled(Icon)`
  color: ${p => p.theme.colors.greyDark};
  stroke: ${p => p.theme.colors.greyDark};
  fill: none;
`;

/** Visually hidden but still keyboard-focusable (0×0 inputs are skipped by Tab). */
const HiddenRadio = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Label = styled.label`
  display: inline-block;
  padding: xs;
  cursor: pointer;
  position: relative;

  &:focus-within {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
    border-radius: 4px;
    z-index: 1;
  }

  &:focus-within::after {
    content: '';
    display: block;
    position: absolute;
    border-radius: 4px;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border: 2px solid var(--color-focus-ring-inner);
    pointer-events: none;
  }
`;

interface Props {
  onChange: ChangeEventHandler<HTMLInputElement>;
  active: boolean;
  checked: boolean;
  value: number;
  name: string;
  ariaLabel: string;
}

export const Star = ({
  onChange,
  active,
  checked,
  value,
  name,
  ariaLabel
}: Props) => (
  <Label>
    <HiddenRadio
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
    />
    {active ? (
      <ActiveIcon aria-hidden="true" focusable="false" />
    ) : (
      <InactiveIcon aria-hidden="true" focusable="false" />
    )}
  </Label>
);
